import { graphql } from 'react-relay';
import { useEffect } from 'react';

import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useAccessLinkPermissionsForObjectShareableLinkPermissionsForObjectQuery$data,
	useAccessLinkPermissionsForObjectShareableLinkPermissionsForObjectQuery$variables,
} from './__generated__/useAccessLinkPermissionsForObjectShareableLinkPermissionsForObjectQuery.graphql.ts';

/**
 * Object type for permission queries. Mirrors the GraphQL ShareableLinkPermissionsObjectType enum.
 */
enum AccessLinkPermissionsObjectType {
	CONTENT = 'CONTENT',
}

/**
 * Permission type for access links. Mirrors the GraphQL ShareableLinkPermissionsType enum.
 */
enum AccessLinkPermissionsType {
	EDIT = 'EDIT',
}

export type AccessLinkPermissionsForObjectData = {
	hasEditPermission: boolean;
	permissions: readonly string[];
};

export type AccessLinkPermissionsForObjectQueryData = {
	isLoading: boolean;
	refetch: () => void;
	error?: Error;
	data?: AccessLinkPermissionsForObjectData;
};

/** @deprecated Use AccessLinkPermissionsForObjectData */
export type ShareableLinkPermissionsForObjectData = AccessLinkPermissionsForObjectData;
/** @deprecated Use AccessLinkPermissionsForObjectQueryData */
export type ShareableLinkPermissionsForObjectQueryData = AccessLinkPermissionsForObjectQueryData;

/**
 * Fetches edit permissions for an access link object (e.g., whiteboard).
 *
 * Queries whether the access link has edit permissions for the given object,
 * which determines if editable public whiteboards are supported.
 */
export const useAccessLinkPermissionsForObject = ({
	objectId,
	objectType = AccessLinkPermissionsObjectType.CONTENT,
	skip,
}: {
	objectId: string;
	objectType?: AccessLinkPermissionsObjectType;
	skip?: boolean;
}): AccessLinkPermissionsForObjectQueryData => {
	const { loading, error, data, refetch } = useQueryInterop<
		useAccessLinkPermissionsForObjectShareableLinkPermissionsForObjectQuery$data,
		useAccessLinkPermissionsForObjectShareableLinkPermissionsForObjectQuery$variables
	>(
		graphql`
			query useAccessLinkPermissionsForObjectShareableLinkPermissionsForObjectQuery(
				$objectId: ID!
				$objectType: PublicLinkPermissionsObjectType!
			) {
				publicLinkPermissionsForObject(objectId: $objectId, objectType: $objectType) {
					permissions
				}
			}
		`,
		{
			variables: {
				objectId,
				objectType,
			},
			skip,
		},
	);

	useEffect(() => {
		if (isUnauthorizedError(error)) {
			markErrorAsHandled(error);
		}
	}, [error]);

	const hasEditPermission: boolean =
		data?.publicLinkPermissionsForObject?.permissions?.includes(AccessLinkPermissionsType.EDIT) ??
		false;

	if (loading) return { isLoading: true, refetch };

	return {
		isLoading: false,
		error,
		refetch,
		data: error
			? undefined
			: {
					hasEditPermission,
					permissions: data?.publicLinkPermissionsForObject?.permissions ?? [],
				},
	};
};
